import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { DumbbellIcon, LeafIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export const HeroSection: React.FC = () => {
  const [mobileImageLoaded, setMobileImageLoaded] = useState(false);
  useEffect(() => {
    // Preload image for mobile devices
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const img = new Image();
      img.src = 'https://cdn.magicpatterns.com/uploads/w7Fkt2oihPnxQqonUM8RYm/Ondra.jpg';
      img.onload = () => {
        setMobileImageLoaded(true);
      };
    } else {
      setMobileImageLoaded(true); // Desktop doesn't need to wait
    }
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // Height of fixed navbar
      const additionalOffset = 20; // Extra spacing for better visibility
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - additionalOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-screen bg-black overflow-hidden pt-20 section section-dark" data-id="element-514">
      <div className="flex flex-col md:flex-row relative z-10" data-id="element-515">
        {/* Mobile hero - full image with overlay text */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: mobileImageLoaded ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="md:hidden w-full relative h-[100vh]" data-id="element-516">
          <img src="https://cdn.magicpatterns.com/uploads/w7Fkt2oihPnxQqonUM8RYm/Ondra.jpg" alt="Transformace těla" className="w-full h-full object-cover" loading="eager" onLoad={() => setMobileImageLoaded(true)} data-id="element-517" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/90" data-id="element-518"></div>

          {/* Signature - Mobile */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1.5,
          delay: 1.2
        }} className="absolute bottom-4 left-4 z-20 flex items-center gap-2" data-id="element-519">
            <div className="w-8 h-[1px] bg-gradient-to-r from-green-500/60 to-transparent" data-id="element-520"></div>
            <p className="text-xl text-green-400/70 drop-shadow-[0_2px_8px_rgba(34,197,94,0.3)]" style={{
            fontFamily: "'Great Vibes', cursive",
            fontWeight: 400
          }} data-id="element-521">
              Janík Ondřej
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: mobileImageLoaded ? 1 : 0,
          y: mobileImageLoaded ? 0 : 20
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="absolute inset-0 flex flex-col justify-center items-center p-6 text-center" data-id="element-522">
            <h1 className="text-3xl font-black mb-4" data-id="element-523">
              <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent" data-id="element-524">
                TRANSFORMUJ
              </span>{' '}
              SVÉ TĚLO
            </h1>
            <p className="text-base font-medium text-white mb-8 max-w-xs" data-id="element-525">
              Nauč se za{' '}
              <span className="text-green-500 font-semibold" data-id="element-526">3 měsíce</span>{' '}
              cvičit tak, aby ses už nikdy nemusel spoléhat na{' '}
              <span className="text-green-500 font-semibold" data-id="element-527">trenéra</span> ani{' '}
              <span className="text-green-500 font-semibold" data-id="element-528">
                subscription plány
              </span>
              .
            </p>
            <div className="flex justify-center w-full max-w-xs" data-id="element-529">
              <Button variant="outline" size="lg" onClick={() => scrollToSection('plan-cards')} icon={<DumbbellIcon className="w-5 h-5" data-id="element-531" />} className="text-sm py-4 border-white hover:bg-green-500 hover:border-green-500 transition-colors" data-id="element-530">
                MÁM ZÁJEM O PLÁN
              </Button>
            </div>
          </motion.div>
        </motion.div>
        {/* Mobile - Additional text below hero */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: mobileImageLoaded ? 1 : 0,
        y: mobileImageLoaded ? 0 : 20
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="md:hidden bg-black px-6 py-10" data-id="element-532">
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-xl md:text-2xl mb-6 font-semibold text-white tracking-wide" data-id="element-533">
            Pokud máš pocit, že:
          </motion.p>
          <motion.ul initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="space-y-5 mb-8 text-lg" data-id="element-534">
            <li className="flex items-start" data-id="element-535">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-536">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-537">
                Cvičíš roky, ale{' '}
                <span className="text-green-500 font-semibold" data-id="element-538">nerosteš</span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-539">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-540">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-541">
                Nevíš, jak{' '}
                <span className="text-green-500 font-semibold" data-id="element-542">
                  správně cvičit
                </span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-543">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-544">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-545">
                Nechceš utrácet za{' '}
                <span className="text-green-500 font-semibold" data-id="element-546">drahé</span>{' '}
                soukromé trénery, nebo subscription plány
              </span>
            </li>
          </motion.ul>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-xl md:text-2xl mb-12 font-semibold text-white tracking-wide" data-id="element-547">
            Pak jsi tady <span className="text-green-500" data-id="element-548">správně</span>.
          </motion.p>
        </motion.div>
        {/* Desktop layout - left side image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8
      }} className="hidden md:block w-full md:w-1/2 h-[50vh] md:h-screen relative" data-id="element-549">
          <img src="https://cdn.magicpatterns.com/uploads/w7Fkt2oihPnxQqonUM8RYm/Ondra.jpg" alt="Transformace těla" className="w-full h-full object-cover object-center" loading="eager" data-id="element-550" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black" data-id="element-551"></div>

          {/* Signature - Desktop */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1.8,
          delay: 1.4
        }} className="absolute bottom-6 left-6 z-20 flex items-center gap-3" data-id="element-552">
            <div className="w-12 h-[1.5px] bg-gradient-to-r from-green-500/70 to-transparent" data-id="element-553"></div>
            <p className="text-3xl text-green-400/80 drop-shadow-[0_2px_12px_rgba(34,197,94,0.4)]" style={{
            fontFamily: "'Great Vibes', cursive",
            fontWeight: 400,
            letterSpacing: '0.03em'
          }} data-id="element-554">
              Janík Ondřej
            </p>
          </motion.div>
        </motion.div>
        {/* Desktop layout - right side content */}
        <div className="hidden md:block w-full md:w-1/2 md:pl-12 container mx-auto px-4 py-8 md:py-20" data-id="element-555">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-3xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight" data-id="element-556">
            <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent" data-id="element-557">
              TRANSFORMUJ
            </span>{' '}
            SVÉ TĚLO
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl mb-6 font-medium text-gray-200" data-id="element-558">
            Nauč se za{' '}
            <span className="text-green-500 font-semibold" data-id="element-559">3 měsíce</span>{' '}
            cvičit tak, aby ses už{' '}
            <span className="text-green-500 font-semibold" data-id="element-560">nikdy</span> nemusel
            spoléhat na své{' '}
            <span className="text-green-500 font-semibold" data-id="element-561">
              dosavadní zkušenosti
            </span>
            , <span className="text-green-500 font-semibold" data-id="element-562">trenéra</span> ani{' '}
            <span className="text-green-500 font-semibold" data-id="element-563">nekonečné</span>{' '}
            <span className="text-green-500 font-semibold" data-id="element-564">
              subscription plány
            </span>
            .
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-xl md:text-2xl mb-6 font-semibold text-white tracking-wide" data-id="element-565">
            Pokud máš pocit, že:
          </motion.p>
          <motion.ul initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="space-y-5 mb-8 text-lg" data-id="element-566">
            <li className="flex items-start" data-id="element-567">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-568">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-569">
                Cvičíš roky, ale{' '}
                <span className="text-green-500 font-semibold" data-id="element-570">nerosteš</span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-571">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-572">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-573">
                Nevíš, jak{' '}
                <span className="text-green-500 font-semibold" data-id="element-574">
                  správně cvičit
                </span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-575">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-576">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-577">
                Nechceš utrácet za{' '}
                <span className="text-green-500 font-semibold" data-id="element-578">drahé</span>{' '}
                soukromé trénery, nebo subscription plány
              </span>
            </li>
          </motion.ul>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-xl md:text-2xl mb-12 font-semibold text-white tracking-wide" data-id="element-579">
            Pak jsi tady <span className="text-green-500" data-id="element-580">správně</span>.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="flex justify-start mt-20" data-id="element-581">
            <Button variant="primary" size="lg" onClick={() => scrollToSection('plan-cards')} icon={<DumbbellIcon className="w-6 h-6" data-id="element-583" />} className="shine px-12 py-5 text-xl" data-id="element-582">
              MÁM ZÁJEM O PLÁN
            </Button>
          </motion.div>
        </div>
      </div>
      <div className="scroll-indicator hidden md:flex" data-id="element-584">
        <span className="text-sm tracking-widest text-green-500 font-medium" data-id="element-585">
          SCROLL
        </span>
      </div>
      <div className="section-divider divider-slant-down" data-id="element-586"></div>
    </section>;
};